import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TRY, NUM } from "@/lib/format";
import { PackageCheck, PackageSearch, Unlink, Percent, Tag, RefreshCw, AlertTriangle } from "lucide-react";
import ProductPicker from "@/components/ProductPicker";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const pct = (n) => `%${Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}`;

// Kalem iskontosu: UBL'den gelen tutar/oran; oran yoksa brütten türetilir
export const lineDiscount = (ln) => {
  const gross = ln.gross ?? r2(Number(ln.quantity || 0) * Number(ln.unit_price || 0));
  const amt = Number(ln.discount || 0) || Math.max(0, r2(gross - Number(ln.line_net ?? gross)));
  const rate = Number(ln.discount_rate || 0) || (gross > 0 && amt > 0 ? r2((amt / gross) * 100) : 0);
  const netUnit = Number(ln.quantity) > 0 ? (Number(ln.line_net ?? gross - amt) / Number(ln.quantity)) : Number(ln.unit_price || 0);
  return { gross, amt, rate, netUnit };
};

const MATCH_LABEL = { memory: "Tedarikçi kod hafızası", code: "Kod / barkod", name: "Birebir ad", manual: "Elle seçildi" };

function MatchCell({ i, m, canEdit, busy, onSet }) {
  if (m.product_id) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-2.5 py-2 text-xs space-y-1" data-testid={`incoming-match-${i}`}>
        <div className="flex items-center gap-1.5 font-bold text-emerald-800"><PackageCheck className="h-3.5 w-3.5 shrink-0" /><span className="truncate" title={m.product_name}>{m.product_name}</span></div>
        <div className="text-emerald-900/70 tabular-nums">{m.product_code && <span className="font-mono">{m.product_code} · </span>}Stok {NUM(m.stock_qty)} {m.product_unit} · Maliyet {TRY(m.cost)}</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className="text-[10px] py-0 border-emerald-300 bg-white text-emerald-700">%{Math.round((m.score ?? 1) * 100)} · {MATCH_LABEL[m.method] || "Elle seçildi"}</Badge>
          {canEdit && <button className="text-[10px] text-muted-foreground hover:text-red-600 inline-flex items-center gap-0.5" onClick={() => onSet(i, null)} disabled={busy} data-testid={`incoming-unmatch-${i}`}><Unlink className="h-3 w-3" />kaldır</button>}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-dashed px-2.5 py-2 text-xs space-y-1.5" data-testid={`incoming-match-${i}`}>
      {m.suggestion ? (
        <div className="text-amber-800 leading-snug"><PackageSearch className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />Öneri: <b title={m.suggestion.product_name}>{m.suggestion.product_name}</b>
          <span className="text-muted-foreground"> (%{Math.round((m.suggestion.score || 0) * 100)} benzer · stok {NUM(m.suggestion.stock_qty)})</span>
          {canEdit && <button className="ml-1.5 font-semibold underline text-[11px]" onClick={() => onSet(i, m.suggestion.product_id)} disabled={busy} data-testid={`incoming-accept-suggestion-${i}`}>kabul et</button>}
        </div>
      ) : <div className="text-muted-foreground">Eşleşme yok → stoğa eklenirse <b>yeni ürün</b> açılır</div>}
      {canEdit && <ProductPicker onSelect={(p) => onSet(i, p.id)} label="Stoktan ürün seç" size="sm" variant="outline" className="h-7 text-[11px] w-full" testid={`incoming-pick-${i}`} />}
    </div>
  );
}

// Gelen e-Fatura / e-İrsaliye kalem tablosu — iskonto belirgin (turuncu), net birim fiyat ve KDV ayrı, en altta özet şeridi
export default function QnbIncomingLines({ d, isFatura, matches, canEditMatch, busy, onSetMatch, onRematch }) {
  const parsed = d?.parsed || {};
  const lines = parsed.lines || [];
  const totals = parsed.totals || {};
  const matchOf = (i) => matches.find((m) => m.line_index === i) || {};
  const matchedCount = matches.filter((m) => m.product_id).length;
  const sum = lines.reduce((a, ln) => { const x = lineDiscount(ln); a.gross += x.gross; a.disc += x.amt; return a; }, { gross: 0, disc: 0 });
  const gross = lines.length ? sum.gross : (totals.line_extension != null && totals.allowance != null ? totals.line_extension + totals.allowance : (totals.line_extension || 0));
  const discountTotal = lines.length ? sum.disc : Number(totals.allowance || 0);
  const discountPct = gross > 0 ? r2((discountTotal / gross) * 100) : 0;
  const hasDiscount = discountTotal > 0.005;

  return (
    <div className="rounded-2xl border bg-card shadow-sm xl:overflow-hidden xl:flex xl:flex-col xl:min-h-0" data-testid="incoming-lines">
      {d?.parse_error && <div className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-800 flex gap-2 shrink-0"><AlertTriangle className="h-4 w-4 shrink-0" />{d.parse_error}</div>}
      <div className="px-4 py-2.5 border-b bg-gradient-to-r from-slate-50 to-white flex items-center gap-2 flex-wrap shrink-0">
        <div className="font-head font-black text-sm tracking-tight">Fatura Kalemleri <span className="text-muted-foreground font-semibold">({lines.length})</span></div>
        {hasDiscount && <Badge className="bg-primary/10 text-primary border border-primary/30 hover:bg-primary/10 text-[11px]" data-testid="incoming-discount-badge"><Percent className="h-3 w-3 mr-1" />İskontolu fatura · {pct(discountPct)} · {TRY(discountTotal)}</Badge>}
        {isFatura && matches.length > 0 && (
          <span className={`ml-auto font-semibold px-2.5 py-0.5 rounded-full text-[11px] ${matchedCount === matches.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`} data-testid="incoming-match-summary">
            <PackageCheck className="h-3 w-3 inline mr-1 -mt-0.5" />{matchedCount}/{matches.length} kalem stokla eşleşti
          </span>
        )}
        {canEditMatch && <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={onRematch} disabled={busy} data-testid="incoming-rematch-btn"><RefreshCw className="h-3 w-3 mr-1" />Yeniden eşleştir</Button>}
      </div>

      <div className="hidden xl:block flex-1 min-h-0 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-800 text-white">
            <tr className="text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-left w-8 font-bold">#</th>
              <th className="px-3 py-2 text-left font-bold">Mal / Hizmet</th>
              <th className="px-3 py-2 text-right font-bold whitespace-nowrap">Miktar</th>
              {isFatura && <>
                <th className="px-3 py-2 text-right font-bold whitespace-nowrap">Liste Fiyatı</th>
                <th className="px-3 py-2 text-right font-bold whitespace-nowrap bg-primary/90">İskonto</th>
                <th className="px-3 py-2 text-right font-bold whitespace-nowrap">Net Birim</th>
                <th className="px-3 py-2 text-right font-bold whitespace-nowrap">KDV</th>
                <th className="px-3 py-2 text-right font-bold whitespace-nowrap">Tutar</th>
                <th className="px-3 py-2 text-left font-bold min-w-[230px]">Stok Eşleşmesi</th>
              </>}
            </tr>
          </thead>
          <tbody>
            {lines.map((ln, i) => {
              const m = matchOf(i);
              const x = lineDiscount(ln);
              const codes = [ln.code && `Kod ${ln.code}`, ln.barcode && ln.barcode !== ln.code && `Barkod ${ln.barcode}`, ln.brand && `Marka ${ln.brand}`].filter(Boolean);
              return (
                <tr key={i} className="border-b last:border-0 odd:bg-white even:bg-slate-50/50 hover:bg-primary/[0.03] align-top" data-testid={`incoming-line-${i}`}>
                  <td className="px-3 py-2.5 text-muted-foreground tabular-nums text-xs">{i + 1}</td>
                  <td className="px-3 py-2.5 min-w-[200px]">
                    <div className="font-semibold leading-snug">{ln.name}</div>
                    {(codes.length > 0 || ln.description) && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {codes.map((c) => <span key={c} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-slate-600"><Tag className="h-2.5 w-2.5" />{c}</span>)}
                        {ln.description && <span className="text-[11px] text-muted-foreground">{ln.description}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap"><b>{NUM(ln.quantity)}</b> <span className="text-[11px] text-muted-foreground uppercase">{ln.unit}</span></td>
                  {isFatura && <>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-muted-foreground">{TRY(ln.unit_price)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap bg-primary/[0.04]" data-testid={`incoming-line-discount-${i}`}>
                      {x.amt > 0.005 ? (
                        <div className="inline-flex flex-col items-end">
                          <span className="inline-flex items-center rounded-md bg-primary text-white text-[11px] font-black px-1.5 py-0.5 tabular-nums">{pct(x.rate)}</span>
                          <span className="text-[11px] text-primary font-semibold tabular-nums mt-0.5">−{TRY(x.amt)}</span>
                        </div>
                      ) : <span className="text-[11px] text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold">{TRY(x.netUnit)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap"><div className="text-[11px] text-muted-foreground">%{NUM(ln.tax_rate)}</div><div className="text-xs">{TRY(ln.tax)}</div></td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap"><div className="font-head font-black">{TRY(ln.line_total)}</div><div className="text-[10px] text-muted-foreground">net {TRY(ln.line_net)}</div></td>
                    <td className="px-3 py-2.5"><MatchCell i={i} m={m} canEdit={canEditMatch} busy={busy} onSet={onSetMatch} /></td>
                  </>}
                </tr>
              );
            })}
            {lines.length === 0 && <tr><td colSpan={9} className="text-center text-sm text-muted-foreground py-8">Kalem bilgisi UBL indirilince görünür.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="xl:hidden divide-y" data-testid="incoming-lines-mobile">
        {lines.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">Kalem bilgisi UBL indirilince görünür.</div>}
        {lines.map((ln, i) => {
          const x = lineDiscount(ln);
          const codes = [ln.code && `Kod ${ln.code}`, ln.barcode && ln.barcode !== ln.code && `Barkod ${ln.barcode}`, ln.brand && `Marka ${ln.brand}`].filter(Boolean);
          return (
            <div key={i} className="p-3 space-y-2" data-testid={`incoming-line-m-${i}`}>
              <div className="flex items-start gap-2">
                <span className="text-[11px] text-muted-foreground tabular-nums mt-0.5">{i + 1}.</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold leading-snug">{ln.name}</div>
                  {(codes.length > 0 || ln.description) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {codes.map((c) => <span key={c} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-slate-600"><Tag className="h-2.5 w-2.5" />{c}</span>)}
                      {ln.description && <span className="text-[11px] text-muted-foreground">{ln.description}</span>}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {isFatura
                    ? <><div className="font-head font-black tabular-nums">{TRY(ln.line_total)}</div><div className="text-[10px] text-muted-foreground uppercase">Tutar</div></>
                    : <><div className="font-head font-black tabular-nums">{NUM(ln.quantity)}</div><div className="text-[10px] text-muted-foreground uppercase">{ln.unit}</div></>}
                </div>
              </div>
              {isFatura && (
                <div className="grid grid-cols-3 gap-y-2 gap-x-3 rounded-lg bg-slate-50 p-2.5">
                  <KV label="Miktar" value={<><b>{NUM(ln.quantity)}</b> <span className="text-[10px] uppercase text-muted-foreground">{ln.unit}</span></>} />
                  <KV label="Liste Fiyatı" value={TRY(ln.unit_price)} />
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">İskonto</div>
                    {x.amt > 0.005
                      ? <div className="leading-tight"><span className="inline-flex items-center rounded bg-primary text-white text-[10px] font-black px-1 py-0.5 tabular-nums">{pct(x.rate)}</span><div className="text-[11px] text-primary font-semibold tabular-nums">−{TRY(x.amt)}</div></div>
                      : <div className="text-[11px] text-muted-foreground">—</div>}
                  </div>
                  <KV label="Net Birim" value={<span className="font-semibold">{TRY(x.netUnit)}</span>} />
                  <KV label={`KDV %${NUM(ln.tax_rate)}`} value={TRY(ln.tax)} />
                  <KV label="Net Tutar" value={TRY(ln.line_net)} />
                </div>
              )}
              {isFatura && <div className="pt-0.5"><MatchCell i={i} m={matchOf(i)} canEdit={canEditMatch} busy={busy} onSet={onSetMatch} /></div>}
            </div>
          );
        })}
      </div>

      {isFatura && totals.payable != null && (
        <div className="border-t bg-slate-900 text-white px-4 py-3 shrink-0" data-testid="incoming-totals">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
            <Tot label="Brüt Toplam" value={TRY(gross)} />
            <div className={`rounded-lg px-3 py-1.5 -my-1.5 ${hasDiscount ? "bg-primary text-white" : "bg-white/5"}`} data-testid="incoming-total-discount">
              <div className="text-[10px] font-bold uppercase tracking-wider opacity-90">İskonto {hasDiscount && `· ${pct(discountPct)}`}</div>
              <div className="font-head font-black text-base tabular-nums">{hasDiscount ? `−${TRY(discountTotal)}` : "—"}</div>
            </div>
            <Tot label="Matrah (KDV hariç)" value={TRY(totals.tax_exclusive)} />
            <Tot label="Toplam KDV" value={TRY(totals.tax)} />
            <div className="text-right sm:text-left">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Ödenecek Tutar</div>
              <div className="font-head font-black text-2xl text-primary tabular-nums leading-none mt-0.5" data-testid="incoming-total-payable">{TRY(totals.payable)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Tot = ({ label, value }) => (
  <div><div className="text-[10px] font-bold uppercase tracking-wider text-white/60">{label}</div><div className="font-semibold tabular-nums">{value}</div></div>
);

const KV = ({ label, value }) => (
  <div><div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-xs tabular-nums">{value}</div></div>
);
